import React, { useEffect, useState } from "react";
import { Table, Button, Form, Spinner, Badge } from "react-bootstrap";
import * as cobrosFamiliaApi from "../../api/cobrosFamilia";
import * as cuentasApi from "../../api/cuentas";
import * as reportesApi from "../../api/reportes";
import ModalConfirmar from "../../components/ModalConfirmar";
import ModalNuevoCobro from "./ModalNuevoCobro";
import ModalAmortizarCobro from "./ModalAmortizarCobro";
import ModalDetalleCobro from "./ModalDetalleCobro";
import { formatoNumero } from "../../utils/moneda";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const HOY = new Date();

export default function CobrosFamiliaPage() {
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const [anio, setAnio] = useState(HOY.getFullYear());
  const [cobros, setCobros] = useState([]);
  const [reporte, setReporte] = useState([]);
  const [deudores, setDeudores] = useState([]);
  const [cuentasTarjeta, setCuentasTarjeta] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [showNuevo, setShowNuevo] = useState(false);
  const [aAmortizar, setAAmortizar] = useState(null);
  const [detalleId, setDetalleId] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);
  const [aCancelarTodo, setACancelarTodo] = useState(null);
  const [aLiquidar, setALiquidar] = useState(null);
  const [aExtornar, setAExtornar] = useState(null);
  const [filtroDeudorId, setFiltroDeudorId] = useState("");

  const cargar = async () => {
    setCargando(true);
    try {
      const [listaCobros, listaReporte] = await Promise.all([
        cobrosFamiliaApi.listarPorMes(mes, anio),
        cobrosFamiliaApi.obtenerReporte(mes, anio),
      ]);
      setCobros(listaCobros);
      setReporte(listaReporte);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [mes, anio]); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarDeudores = () => cobrosFamiliaApi.listarDeudores().then(setDeudores);

  const cobrosFiltrados = filtroDeudorId
    ? cobros.filter((c) => c.deudorId === Number(filtroDeudorId))
    : cobros;

  useEffect(() => {
    cargarDeudores();
    cuentasApi.listarCuentas().then((cs) => setCuentasTarjeta(cs.filter((c) => c.tipo === "TarjetaCredito")));
  }, []);

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h4 className="mb-0">Cobros Familia</h4>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Form.Select size="sm" style={{ width: 130 }} value={mes} onChange={(e) => setMes(Number(e.target.value))}>
            {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </Form.Select>
          <Form.Select size="sm" style={{ width: 100 }} value={anio} onChange={(e) => setAnio(Number(e.target.value))}>
            {[HOY.getFullYear() - 1, HOY.getFullYear(), HOY.getFullYear() + 1].map((a) => <option key={a} value={a}>{a}</option>)}
          </Form.Select>
          <Button size="sm" variant="outline-primary" onClick={() => reportesApi.descargarCobrosFamiliaPdf(mes, anio)}>
            <i className="bi bi-file-earmark-pdf me-1"></i>Reporte PDF
          </Button>
          <Button size="sm" variant="primary" onClick={() => setShowNuevo(true)}>
            <i className="bi bi-plus-lg me-1"></i>Nuevo cobro
          </Button>
        </div>
      </div>

      {cargando ? <Spinner animation="border" /> : (
        <>
          <h5 className="mb-3">Resumen del mes por miembro</h5>
          <div className="fs-card p-0 overflow-hidden mb-4">
            <Table responsive hover className="table-fs mb-0 align-middle">
              <thead>
                <tr>
                  <th>Miembro</th><th className="text-end">Total debido</th>
                  <th className="text-end">Amortizado</th><th className="text-end">Cancelado</th><th className="text-end">Pendiente</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {reporte.map((r) => (
                  <tr key={r.deudorId}>
                    <td>{r.deudor}</td>
                    <td className="text-end">${formatoNumero(r.totalDebido)}</td>
                    <td className="text-end text-fs-ingreso">${formatoNumero(r.totalAmortizado)}</td>
                    <td className="text-end">${formatoNumero(r.totalCancelado)}</td>
                    <td className="text-end fw-semibold text-fs-gasto">${formatoNumero(r.totalPendiente)}</td>
                    <td className="text-end">
                      {cobros.some((c) => c.deudorId === r.deudorId && c.estado === "Pendiente") && (
                        <Button size="sm" variant="outline-success" className="me-1" onClick={() => setACancelarTodo(r)}>
                          <i className="bi bi-check2-all me-1"></i>Cancelar todo
                        </Button>
                      )}
                      {r.totalCancelado > 0 && !cobros.some((c) => c.deudorId === r.deudorId && c.estado !== "Cancelada") && (
                        <Button size="sm" variant="outline-secondary" title="Archivar los cobros ya cancelados: dejan de mostrarse este mes" onClick={() => setALiquidar(r)}>
                          <i className="bi bi-archive me-1"></i>Liquidar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!reporte.length && (
                  <tr><td colSpan={6} className="text-center text-muted py-4">Sin datos este mes.</td></tr>
                )}
                {!!reporte.length && (
                  <tr className="fw-bold">
                    <td>TOTAL</td>
                    <td className="text-end">${formatoNumero(reporte.reduce((acc, r) => acc + r.totalDebido, 0))}</td>
                    <td className="text-end text-fs-ingreso">${formatoNumero(reporte.reduce((acc, r) => acc + r.totalAmortizado, 0))}</td>
                    <td className="text-end">${formatoNumero(reporte.reduce((acc, r) => acc + r.totalCancelado, 0))}</td>
                    <td className="text-end text-fs-gasto">${formatoNumero(reporte.reduce((acc, r) => acc + r.totalPendiente, 0))}</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-end mb-2">
            <Form.Select size="sm" style={{ width: 200 }} value={filtroDeudorId} onChange={(e) => setFiltroDeudorId(e.target.value)}>
              <option value="">Todos los miembros</option>
              {deudores.map((d) => <option key={d.deudorId} value={d.deudorId}>{d.nombre}</option>)}
            </Form.Select>
          </div>
          <div className="fs-card p-0 overflow-hidden">
            <Table responsive hover className="table-fs table-fs-responsive mb-0 align-middle">
              <thead>
                <tr>
                  <th>Miembro</th><th>Tipo</th><th>Descripción</th>
                  <th className="text-end">Monto</th><th className="text-end">Saldo</th><th>Estado</th><th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cobrosFiltrados.map((c) => (
                  <tr key={c.cobroId}>
                    <td data-label="Miembro">{c.deudor}</td>
                    <td data-label="Tipo">
                      <i className={`bi ${c.tipo === "TarjetaCredito" ? "bi-credit-card" : "bi-cash"} me-1 text-muted`}></i>
                      {c.tipo === "TarjetaCredito" ? "Tarjeta" : "Efectivo"}
                    </td>
                    <td data-label="Descripción">{c.descripcion}</td>
                    <td data-label="Monto" className="text-end">${formatoNumero(c.monto)}</td>
                    <td data-label="Saldo" className="text-end fw-semibold text-fs-gasto">${formatoNumero(c.saldoPendiente)}</td>
                    <td data-label="Estado">
                      <Badge
                        bg={c.estado === "Cancelada" ? "success" : c.estado === "En espera" ? "secondary" : "warning"}
                        text={c.estado === "Pendiente" ? "dark" : undefined}
                      >
                        {c.estado}
                      </Badge>
                    </td>
                    <td data-label="Acciones" className="text-end">
                      <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setDetalleId(c.cobroId)}>
                        <i className="bi bi-eye"></i>
                      </Button>
                      {c.estado === "Pendiente" && (
                        <>
                          <Button size="sm" variant="outline-primary" className="me-1" onClick={() => setAAmortizar(c)}>
                            <i className="bi bi-cash-coin"></i>
                          </Button>
                          <Button size="sm" variant="outline-success" className="me-1" onClick={async () => { await cobrosFamiliaApi.cancelar(c.cobroId); cargar(); }}>
                            <i className="bi bi-check2-circle"></i>
                          </Button>
                          <Button size="sm" variant="outline-secondary" className="me-1" title="Poner en espera (no cuenta en el resumen)" onClick={async () => { await cobrosFamiliaApi.cambiarEstado(c.cobroId, "En espera"); cargar(); }}>
                            <i className="bi bi-pause-circle"></i>
                          </Button>
                        </>
                      )}
                      {c.estado === "En espera" && (
                        <Button size="sm" variant="outline-warning" className="me-1" title="Volver a Pendiente" onClick={async () => { await cobrosFamiliaApi.cambiarEstado(c.cobroId, "Pendiente"); cargar(); }}>
                          <i className="bi bi-play-circle"></i>
                        </Button>
                      )}
                      {c.estado === "Cancelada" && (
                        <Button size="sm" variant="outline-warning" className="me-1" title="Extornar (volver a Pendiente)" onClick={() => setAExtornar(c)}>
                          <i className="bi bi-arrow-counterclockwise"></i>
                        </Button>
                      )}
                      <Button size="sm" variant="outline-danger" onClick={() => setAEliminar(c)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
                {!cobrosFiltrados.length && (
                  <tr><td colSpan={7} className="text-center text-muted py-4">No hay cobros registrados este mes.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      <ModalNuevoCobro
        show={showNuevo}
        onHide={() => setShowNuevo(false)}
        deudores={deudores}
        cuentasTarjeta={cuentasTarjeta}
        mes={mes}
        anio={anio}
        onGuardado={() => { setShowNuevo(false); cargar(); }}
        onDeudorCreado={cargarDeudores}
      />
      <ModalAmortizarCobro
        show={!!aAmortizar}
        cobro={aAmortizar}
        onHide={() => setAAmortizar(null)}
        onGuardado={() => { setAAmortizar(null); cargar(); }}
      />
      <ModalDetalleCobro
        show={!!detalleId}
        cobroId={detalleId}
        onHide={() => setDetalleId(null)}
      />
      <ModalConfirmar
        show={!!aEliminar}
        titulo="Eliminar cobro"
        mensaje={`¿Eliminar el cobro "${aEliminar?.descripcion}" de ${aEliminar?.deudor}? Esta acción no se puede deshacer.`}
        onConfirmar={async () => { await cobrosFamiliaApi.eliminar(aEliminar.cobroId); setAEliminar(null); cargar(); }}
        onCancelar={() => setAEliminar(null)}
      />
      <ModalConfirmar
        show={!!aCancelarTodo}
        titulo="Cancelar todos los cobros pendientes"
        mensaje={`¿Cancelar todos los cobros Pendiente de ${aCancelarTodo?.deudor} en ${MESES[mes - 1]} ${anio}? Puedes extornarlos individualmente después si te equivocas.`}
        textoConfirmar="Cancelar todo"
        onConfirmar={async () => { await cobrosFamiliaApi.cancelarTodosPorDeudor(aCancelarTodo.deudorId, mes, anio); setACancelarTodo(null); cargar(); }}
        onCancelar={() => setACancelarTodo(null)}
      />
      <ModalConfirmar
        show={!!aLiquidar}
        titulo="Liquidar cobros cancelados"
        mensaje={`¿Liquidar a ${aLiquidar?.deudor}? Los $${formatoNumero(aLiquidar?.totalCancelado || 0)} ya cancelados dejarán de mostrarse este mes (no se borran, solo se archivan). Lo que se registre de aquí en adelante se seguirá mostrando con normalidad.`}
        textoConfirmar="Liquidar"
        onConfirmar={async () => { await cobrosFamiliaApi.liquidarTodosPorDeudor(aLiquidar.deudorId, mes, anio); setALiquidar(null); cargar(); }}
        onCancelar={() => setALiquidar(null)}
      />
      <ModalConfirmar
        show={!!aExtornar}
        titulo="Extornar cobro"
        mensaje={`¿Extornar "${aExtornar?.descripcion}" de ${aExtornar?.deudor}? Volverá a Pendiente y se eliminará cualquier abono ya registrado sobre él (quedará debiendo el monto completo otra vez).`}
        textoConfirmar="Extornar"
        onConfirmar={async () => { await cobrosFamiliaApi.extornar(aExtornar.cobroId); setAExtornar(null); cargar(); }}
        onCancelar={() => setAExtornar(null)}
      />
    </div>
  );
}
